import React from 'react'
import BudgetTableRow from './BudgetTableRow'
import { selectRenderedBudgetColumnDefs } from './budgetTableColumnUtils'
import { CLIENT_ALWAYS_HIDDEN_COLUMNS } from './BudgetDetailsPanelConfig'

function HeaderSwitch({ enabled, onClick }) {
  return (
    <button type="button" onClick={onClick} className={`h-5 w-10 rounded-full p-0.5 transition-colors ${enabled ? 'bg-rb-green' : 'bg-gray-300'}`}>
      <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  )
}

function rowKeyForBudgetTable(row, rowIndex, keyPrefix) {
  if (row.actionId != null) return `${keyPrefix}action-${row.actionId}`
  if (row.customFieldId != null) return `${keyPrefix}custom-field-${row.customFieldId}`
  if (row.influencerId != null) return `${keyPrefix}inf-${row.influencerId}-${Number(row.slotNumber) || 1}`
  return `${keyPrefix}${rowIndex}-${row.influencer || row.instagram || 'row'}`
}

export default function BudgetDataTable({
  config,
  visibleColumns,
  draftColumns,
  editingColumns,
  onToggleColumn,
  rows: rowsProp,
  onInputCellChange,
  tableKind,
  forcedHiddenColumns,
  campaignScenario,
  showInfluencerVisibilityToggle,
  onDuplicateRow,
  onDeleteRow,
  className = '',
  keyPrefix = '',
  forceRowsReadOnly = false,
  hideWhenEmpty = false,
}) {
  if (!config?.columns?.length) return null

  const safeVisible = visibleColumns && typeof visibleColumns === 'object' ? visibleColumns : {}
  const safeDraft = draftColumns && typeof draftColumns === 'object' ? draftColumns : safeVisible

  const eligibleColumns = config.columns.filter(col => !forcedHiddenColumns[col.key])
  const renderedColumnDefs = selectRenderedBudgetColumnDefs(config, forcedHiddenColumns, editingColumns, draftColumns, visibleColumns)

  const renderedKeys = renderedColumnDefs.map(col => col.key)
  const dataRows = Array.isArray(rowsProp) ? rowsProp : config.rows || []
  if (hideWhenEmpty && !dataRows.length) return null

  return (
    <div className={`rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-rb-dark-mode-background${className ? ` ${className}` : ''}`}>
      <div className="overflow-x-auto">
      <table className="w-max min-w-full text-xs">
        <thead>
          <tr className="border border-gray-200 bg-rb-grey dark:border-gray-700">
            {config.groups.map(group => {
              const colSpan = group.keys.filter(key => renderedKeys.includes(key)).length
              if (!colSpan) return null
              return (
                <th key={group.label} colSpan={colSpan} className={`py-3 px-2 border border-gray-200 text-center font-semibold whitespace-nowrap ${group.dark ? 'bg-gray-800 text-white' : ''}`}>
                  {group.label}
                </th>
              )
            })}
          </tr>
          <tr className="border border-gray-200 dark:border-gray-700 text-[11px]">
            {renderedColumnDefs.map(col => {
              const isDimmed = editingColumns && !safeDraft[col.key] && !CLIENT_ALWAYS_HIDDEN_COLUMNS.has(col.key)
              return (
                <th
                  key={col.key}
                  className={`py-2 px-2 border border-gray-200 text-center font-medium whitespace-nowrap ${col.dark ? 'bg-gray-800 text-white' : 'text-gray-600 dark:text-gray-300'} ${col.className || ''} ${isDimmed ? 'opacity-40' : ''}`}
                >
                {col.label}
                </th>
              )
            })}
          </tr>
          {editingColumns && onToggleColumn && (
            <tr className="border border-gray dark:border-gray-700 bg-white">
              {eligibleColumns.map(col => (
                <th key={`${keyPrefix}switch-${col.key}`} className="py-1 px-1 text-center">
                  {!CLIENT_ALWAYS_HIDDEN_COLUMNS.has(col.key) && (
                    <HeaderSwitch enabled={!!safeDraft[col.key]} onClick={() => onToggleColumn(col.key)} />
                  )}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {dataRows.map((row, rowIndex) => (
            <BudgetTableRow
              key={rowKeyForBudgetTable(row, rowIndex, keyPrefix)}
              renderedColumnDefs={renderedColumnDefs}
              row={row}
              rowIndex={rowIndex}
              editingColumns={forceRowsReadOnly ? false : editingColumns}
              draftColumns={safeDraft}
              onInputCellChange={forceRowsReadOnly ? undefined : onInputCellChange}
              tableKind={tableKind}
              campaignScenario={campaignScenario}
              showInfluencerVisibilityToggle={showInfluencerVisibilityToggle}
              onDuplicateRow={forceRowsReadOnly ? undefined : onDuplicateRow}
              onDeleteRow={forceRowsReadOnly ? undefined : onDeleteRow}
            />
          ))}
          {config.footer && !forceRowsReadOnly && (
            <tr className="bg-gray-900 text-white">
              {renderedColumnDefs.map((col, index) => {
                const byKey = config.footer[col.key]
                const cell =
                  byKey !== undefined && byKey !== null && String(byKey).length > 0
                    ? byKey
                    : index === 0 && config.footer.label
                      ? config.footer.label
                      : ''
                return (
                  <td
                    key={`footer-${col.key}`}
                    className={`py-3 px-2 border border-gray-200 text-center font-semibold ${col.key === 'influencer' ? 'text-left' : ''} ${editingColumns && !safeDraft[col.key] && !CLIENT_ALWAYS_HIDDEN_COLUMNS.has(col.key) ? 'opacity-40' : ''}`}
                  >
                    {cell}
                  </td>
                )
              })}
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}
